import { Section, Text } from "@react-email/components";
import useThemeContextualColors, {
  sectionToParent,
} from "../../hooks/use-theme-contextual-colors";
import { tLayout } from "../../translations";
import formatTemplate from "../../lib/format-template";
import { useReturnVariableValues } from "../../hooks/use-return-variable-values";
import {
  INSTRUCTION_BY_ID,
  resolveInstructionKey,
  resolveOverride,
} from "./instructions";
import type { ReturnShippingInstructionsBlockProps } from "./types";

export function ReturnShippingInstructionsEmailRenderer({
  block,
  formatter,
  theme,
  section,
  locale,
  defaultLocale,
}: ReturnShippingInstructionsBlockProps) {
  const colors = useThemeContextualColors(theme, sectionToParent(section));
  const t = tLayout(locale, defaultLocale);
  const { getShippingInstructions } = useReturnVariableValues();
  const { carrierCode, hasLogisticLabel } = getShippingInstructions();

  // Default copy is picked by carrier code (or the return-slip case); the
  // merchant override is keyed by the raw carrier code, with a legacy-key
  // fallback for overrides saved before the raw-code scheme.
  const activeKey = resolveInstructionKey(carrierCode, hasLogisticLabel);
  const definition = INSTRUCTION_BY_ID[activeKey];

  const override = resolveOverride(
    block.content,
    locale,
    defaultLocale,
    carrierCode,
    hasLogisticLabel,
  );

  if (override) {
    return (
      <Section className="px-4">
        <div
          className="m-0 text-base leading-[1.5] text-center"
          style={{ color: colors.text }}
          dangerouslySetInnerHTML={{
            __html: formatter
              ? formatter(formatTemplate(override))
              : formatTemplate(override),
          }}
        />
      </Section>
    );
  }

  // Default copy from the block translations (tLayout has no per-key fallback).
  const translated = t(definition.tLayoutKey);
  const text =
    translated === definition.tLayoutKey ? definition.fallback : translated;

  return (
    <Section className="px-4">
      <Text
        className="m-0 text-base leading-[1.5] text-center"
        style={{ color: colors.text }}
      >
        {text}
      </Text>
    </Section>
  );
}
